import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Briefcase, MapPin, Building, Calendar, FileText, Send,
  RefreshCw, CheckCircle, Search, Clock
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const AnnoncesEmploi = ({ user }) => {
  const [annonces, setAnnonces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnonce, setSelectedAnnonce] = useState(null);
  const [showPostulerDialog, setShowPostulerDialog] = useState(false);
  const [candidatureComment, setCandidatureComment] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadAnnonces();
  }, []);

  const loadAnnonces = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/offres-emploi/actives');
      setAnnonces(response.data || []);
    } catch (error) {
      setAnnonces([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePostuler = async () => {
    if (!selectedAnnonce) return;
    
    setSending(true);
    try {
      await axios.post(`/offres-emploi/${selectedAnnonce.offre_id}/postuler`, {
        commentaire: candidatureComment
      });
      toast.success('Candidature envoyée avec succès !');
      setShowPostulerDialog(false);
      setSelectedAnnonce(null);
      setCandidatureComment('');
      loadAnnonces();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const openPostulerDialog = (annonce) => {
    setSelectedAnnonce(annonce);
    setCandidatureComment('');
    setShowPostulerDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-[#8DC63F]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="annonces-emploi-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
          <Briefcase className="h-7 w-7 text-blue-600" />
          Offres d'emploi
        </h1>
        <p className="text-slate-500 mt-1">
          Consultez les offres des entreprises et postulez directement
        </p>
      </div>

      {/* Liste des annonces */}
      {annonces.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Search className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700">Aucune offre disponible</h3>
            <p className="text-slate-500 mt-2">
              Il n'y a pas d'offre d'emploi publiée pour le moment.<br />
              Revenez plus tard pour voir les nouvelles opportunités.
            </p>
            <Button 
              onClick={loadAnnonces} 
              variant="outline" 
              className="mt-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Actualiser
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {annonces.map(annonce => (
            <Card 
              key={annonce.offre_id} 
              className={`transition-all hover:shadow-lg ${annonce.deja_postule ? 'border-green-200 bg-green-50/30' : 'hover:border-blue-300'}`}
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Briefcase className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-slate-900">{annonce.titre}</h3>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-600">
                          <span className="flex items-center gap-1">
                            <Building className="h-4 w-4" />
                            {annonce.entreprise_nom}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            {annonce.lieu}
                          </span>
                          <Badge variant="outline" className="font-normal">
                            {annonce.type_contrat}
                          </Badge>
                          {annonce.date_debut_souhaitee && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <Calendar className="h-4 w-4" />
                              Début: {new Date(annonce.date_debut_souhaitee).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <p className="mt-4 text-slate-700 whitespace-pre-wrap line-clamp-3">
                      {annonce.description}
                    </p>
                  </div>

                  <div className="flex flex-col items-end gap-2">
                    {annonce.deja_postule ? (
                      <div className="flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg">
                        <CheckCircle className="h-5 w-5" />
                        <span className="font-medium">Candidature envoyée</span>
                      </div>
                    ) : (
                      <Button 
                        onClick={() => openPostulerDialog(annonce)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        Postuler
                      </Button>
                    )}
                    <span className="text-xs text-slate-400">
                      Publiée le {new Date(annonce.created_at).toLocaleDateString('fr-FR')}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Postuler */}
      <Dialog open={showPostulerDialog} onOpenChange={setShowPostulerDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              Postuler à cette offre
            </DialogTitle>
            <DialogDescription>
              Votre profil et votre CV seront envoyés automatiquement
            </DialogDescription>
          </DialogHeader>
          
          {selectedAnnonce && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-slate-900">{selectedAnnonce.titre}</h4>
                <div className="flex items-center gap-3 mt-1 text-sm text-slate-600">
                  <span>{selectedAnnonce.entreprise_nom}</span>
                  <span>•</span>
                  <span>{selectedAnnonce.lieu}</span>
                  <span>•</span>
                  <span>{selectedAnnonce.type_contrat}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Message de motivation (optionnel)
                </label>
                <textarea
                  value={candidatureComment}
                  onChange={(e) => setCandidatureComment(e.target.value)}
                  placeholder="Présentez-vous en quelques lignes, expliquez pourquoi ce poste vous intéresse..."
                  className="w-full min-h-[120px] px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                />
              </div>

              <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
                <p className="font-medium mb-1">Ce qui sera envoyé :</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Vos informations de profil (nom, email, téléphone, ville)</li>
                  <li>Votre année de diplôme</li>
                  <li>Votre CV (si disponible)</li>
                  <li>Votre message ci-dessus</li>
                </ul>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPostulerDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handlePostuler}
              disabled={sending}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {sending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Envoyer ma candidature
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AnnoncesEmploi;
